/**
 * Persisted attribution params carry the moment they were stored so a stale ad
 * click stops attributing signups months later, and so a shared device does not
 * credit the previous person's click to the next person's signup.
 *
 * 90 days matches Google's own `_gcl_aw` cookie window — the longest click
 * window we upload conversions against. Shorten it only together with the ad
 * platform conversion windows, or conversions start dropping.
 */
const PERSISTED_PARAM_TTL_DAYS = 90;

export const PERSISTED_PARAM_TTL_MS = PERSISTED_PARAM_TTL_DAYS * 24 * 60 * 60 * 1000;

export interface StampedParam {
  savedAt: number;
  value: string;
}

/** Entries written before stamping shipped are bare strings. */
export type StoredParam = StampedParam | string | null;

export interface StoredParamRead {
  /** Stored value dropped because it aged past the TTL. */
  isExpired: boolean;
  /** Entry carries no usable timestamp, so it can never expire until rewritten. */
  isUnstamped: boolean;
  /**
   * The string actually sitting in storage, even when `value` is blanked for
   * expiry. Cookie fallbacks compare against this so a still-present `_ttp` /
   * `ttclid` cannot restamp `savedAt` and reset the 90-day window.
   */
  rawValue: string;
  value: string;
}

export function stampParam(value: string, now: number): StampedParam {
  return { value, savedAt: now };
}

function readValue(stored: StoredParam): string {
  if (typeof stored === 'string') {
    return stored;
  }

  if (stored && typeof stored.value === 'string') {
    return stored.value;
  }

  return '';
}

function readSavedAt(stored: StoredParam): number | null {
  if (!stored || typeof stored === 'string' || !Number.isFinite(stored.savedAt)) {
    return null;
  }

  return stored.savedAt;
}

export function readStoredParam(stored: StoredParam, now: number): StoredParamRead {
  const value = readValue(stored);
  const savedAt = readSavedAt(stored);

  if (savedAt === null) {
    return { value, rawValue: value, isExpired: false, isUnstamped: Boolean(value) };
  }

  if (now - savedAt > PERSISTED_PARAM_TTL_MS) {
    return { value: '', rawValue: value, isExpired: true, isUnstamped: false };
  }

  return { value, rawValue: value, isExpired: false, isUnstamped: false };
}
